import React, { useContext, useEffect } from 'react';
import { X } from 'lucide-react';
import { ApplyContext } from '../context/ApplyContext';
import CONFIG from '../config';

const GlobalApplyModal = () => {
  const { isApplyModalOpen, closeApplyModal, selectedJob, applicationData, handleApplicationChange, statusAlert, setStatusAlert } = useContext(ApplyContext);

  useEffect(() => {
    if (isApplyModalOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isApplyModalOpen]);

  if (!isApplyModalOpen) return null;

  const apiBase = CONFIG.API_BASE;

  const downloadEmailDraft = async () => {
    try {
      const formData = new FormData();
      formData.append('fullName', applicationData.fullName);
      formData.append('email', applicationData.email);
      formData.append('phone', applicationData.phone);
      formData.append('coverLetter', applicationData.coverLetter);
      formData.append('jobTitle', selectedJob?.title || 'Application');
      formData.append('jobDepartment', selectedJob?.department || '');
      formData.append('jobLocation', selectedJob?.location || '');
      formData.append('jobType', selectedJob?.type || '');
      if (applicationData.resumeFile) {
        formData.append('resume', applicationData.resumeFile);
      }

      const url = `${apiBase}/apply.php`;

      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      // Try to parse JSON, but fall back to raw text when response is not JSON
      let json = null;
      try {
        json = await response.json();
      } catch (parseErr) {
        try {
          const text = await response.text();
          json = { message: text || response.statusText || 'Server error', raw: text };
        } catch (tErr) {
          json = { message: response.statusText || 'Server error' };
        }
      }

      if (!response.ok || (json && json.success === false)) {
        const err = new Error(json.message || 'Failed to send application');
        err.server = json;
        throw err;
      }
      return json;
    } catch (err) {
      console.error('Error sending application:', err);
      throw err;
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();

    try {
      const result = await downloadEmailDraft();
      if (result && result.message) {
        if (result.url) {
          setStatusAlert({ type: 'success', message: result.message, link: result.url });
        } else if (result.error) {
          setStatusAlert({ type: 'error', message: result.message, link: '' });
        } else {
          setStatusAlert({ type: 'success', message: result.message, link: '' });
        }
      } else {
        setStatusAlert({ type: 'success', message: 'Application sent successfully!', link: '' });
      }
      closeApplyModal();
    } catch (err) {
      console.error('Failed to send application', err);
      const serverMsg = err && err.server && err.server.message ? err.server.message : null;
      const link = err && err.server && err.server.url ? err.server.url : '';
      setStatusAlert({
        type: 'error',
        message: serverMsg || err.message || 'Failed to send application. Please try again.',
        link
      });
      // keep modal open so user can retry or fix inputs
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-end overflow-y-auto bg-black/50 p-4 md:p-6">
        <div className="relative w-full max-w-xl rounded-[1.5rem] border border-slate-200 bg-white p-8 shadow-2xl shadow-slate-900/10 sm:w-[34rem]">
          <button
            onClick={closeApplyModal}
            className="absolute right-4 top-4 rounded-full border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
            aria-label="Close application form"
          >
            <X className="h-5 w-5" />
          </button>

          <div>
            <h2 className="text-3xl font-bold mb-2">
              {selectedJob ? `Apply for ${selectedJob.title}` : 'Apply Now'}
            </h2>
            <p className="text-gray-600 text-sm mb-8">
              Submit your application and resume below.
            </p>

            <form onSubmit={handleApplySubmit} className="space-y-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={applicationData.fullName}
                  onChange={(e) => handleApplicationChange('fullName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5C1F33]"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={applicationData.email}
                  onChange={(e) => handleApplicationChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5C1F33]"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={applicationData.phone}
                  onChange={(e) => handleApplicationChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5C1F33]"
                  required
                />
              </div>

              {/* Cover Letter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cover Letter (Optional)
                </label>
                <textarea
                  value={applicationData.coverLetter}
                  onChange={(e) => handleApplicationChange('coverLetter', e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5C1F33] resize-none"
                />
              </div>

              {/* Resume */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resume
                </label>
                <input
                  type="file"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleApplicationChange('resumeFile', e.target.files[0]);
                      handleApplicationChange('resumeFileName', e.target.files[0].name);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5C1F33]"
                />
                {applicationData.resumeFileName && (
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {applicationData.resumeFileName}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-[#5C1F33] text-white py-3 rounded-lg font-medium hover:bg-[#800020] transition"
              >
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Alert */}
      {statusAlert.message && (
        <div className="fixed right-4 top-24 z-50 w-full max-w-sm rounded-3xl border px-4 py-3 text-sm shadow-2xl sm:right-6 sm:top-24">
          <div
            role="status"
            aria-live="polite"
            className={`rounded-2xl border px-4 py-3 text-sm ${
              statusAlert.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : statusAlert.type === 'error'
                ? 'border-rose-200 bg-rose-50 text-rose-800'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            <div>{statusAlert.message}</div>
            {statusAlert.link && (
              <div className="mt-2">
                <a
                  href={statusAlert.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  View saved resume
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalApplyModal;
